// API Helper - Central helper for API calls and token management
// Works in both Class Components and Function Components

/**
 * Gets the API token from environment variables
 * Works everywhere: Class Components, Function Components, outside of React
 * @returns API Token string
 */
export const getApiToken = (): string | null => {
    return process.env.VITE_API_TOKEN || null;
};

/**
 * Creates a complete API URL with token
 * @param baseUrl - The base URL of the API
 * @param endpoint - The endpoint (optional)
 * @returns Complete URL with token parameter
 */
export const buildApiUrl = (baseUrl: string | undefined, endpoint?: string): string => {
    if (!baseUrl) {
        throw new Error('Base URL is required');
    }

    const token = getApiToken();
    const url = endpoint ? `${baseUrl}${endpoint}` : baseUrl;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}token=${token}`;
};

/**
 * Creates Axios-compatible configuration with token for GET requests
 * @param baseUrl - The base URL of the API
 * @param endpoint - The endpoint (optional)
 * @param params - Query parameters to append to URL (optional)
 * @param additionalHeaders - Additional HTTP headers (optional)
 * @returns Object with URL and headers for Axios
 */
export const buildGetRequest = <T extends Record<string, string | number | boolean | null | undefined>>(
    baseUrl: string | undefined,
    endpoint?: string,
    params?: T,
    additionalHeaders?: Record<string, string>
) => {
    if (!baseUrl) {
        throw new Error('Base URL is required');
    }

    let url = buildApiUrl(baseUrl, endpoint);

    // Add query parameters if provided
    if (params) {
        const queryParams = new URLSearchParams();
        Object.keys(params).forEach((key) => {
            if (params[key] !== undefined && params[key] !== null) {
                queryParams.append(key, String(params[key]));
            }
        });
        const paramString = queryParams.toString();
        if (paramString) {
            url += (url.includes('?') ? '&' : '?') + paramString;
        }
    }

    return {
        url,
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            ...additionalHeaders,
        },
    };
};

/**
 * Creates a POST request configuration with token
 * @param baseUrl - The base URL of the API
 * @param endpoint - The endpoint
 * @param data - POST data
 * @param additionalHeaders - Additional HTTP headers (optional)
 * @returns Complete Axios configuration for POST
 */
export const buildPostRequest = <T>(
    baseUrl: string | undefined,
    endpoint: string,
    data: T,
    additionalHeaders?: Record<string, string>
) => {
    if (!baseUrl) {
        throw new Error('Base URL is required');
    }

    return {
        url: buildApiUrl(baseUrl, endpoint),
        method: 'POST',
        data,
        headers: {
            'Content-Type': 'application/json',
            ...additionalHeaders,
        },
    };
};
